import React, { useMemo, useState } from 'react'
import { Link, navigate } from '@reach/router'
import { Flag, Role } from '@api'

import { LDFlagSet } from 'launchdarkly-js-client-sdk'
import css from './navigation.module.scss'
import { MenuItem, Icon, NavPanel, Tag } from '@runeffective/evo-design-system'
import { auto } from '@popperjs/core'
import useGetNavItems from './navItems'
export interface INavItem {
  label: string
  permitted: Role[]
  isOpen?: boolean
  icon?: string
  path?: string
  tag?: string
  childNodes?: INavItem[]
  flags?: Flag[]
}

const filterByFlag = (flags: LDFlagSet | undefined, route: INavItem) =>
  typeof route.flags === 'undefined' ||
  !route.flags.length ||
  (!!flags && route.flags.every((flag) => flags[flag]))

function filterNav(role: Role, flags: LDFlagSet | undefined, items: INavItem[]): INavItem[] {
  return items.reduce((acc: INavItem[], item: INavItem) => {
    const isAllowed = item.permitted.includes(role) && filterByFlag(flags, item)

    if (isAllowed) {
      if (item.childNodes) {
        acc.push({
          ...item,
          childNodes: filterNav(role, flags, item.childNodes),
        })
      } else {
        acc.push(item)
      }
    }

    return acc
  }, [])
}

function Item({ id, item, activeMenu, setActiveMenu, activeMenuIcon, setActiveMenuIcon }: any) {
  if (item.childNodes) {
    return (
      <MenuItem
        title={item.label}
        active={item.label + id === activeMenuIcon}
        onlyIcon={item.label + id === activeMenuIcon}
        leftAdornment={<Icon iconName={item.icon} iconStyle="fal" size="sm" />}
      >
        {item.childNodes.map((node: INavItem, key: number) => {
          if (node?.childNodes) {
            return (
              <MenuItem title={node.label} key={key}>
                {node?.childNodes.map((childNode: INavItem, key) => {
                  return childNode?.tag ? (
                    <MenuItem
                      key={key}
                      tag={<Tag title={childNode?.tag} variant="Informational" />}
                      title={childNode.label}
                      active={childNode.label + id === activeMenu}
                      onClick={() => {
                        setActiveMenu(childNode.label + id)
                        setActiveMenuIcon(item.label + id)
                        navigate(`/${childNode.path}`)
                      }}
                    />
                  ) : (
                    <MenuItem
                      key={key}
                      title={childNode.label}
                      active={childNode.label + id === activeMenu}
                      onClick={() => {
                        setActiveMenu(childNode.label + id)
                        setActiveMenuIcon(item.label + id)
                        navigate(`/${childNode.path}`)
                      }}
                    />
                  )
                })}
              </MenuItem>
            )
          } else {
            return node?.tag ? (
              <MenuItem
                key={key}
                tag={<Tag title={node?.tag} variant="Informational" />}
                title={node.label}
                active={node.label + id === activeMenu}
                onClick={() => {
                  setActiveMenu(node.label + id)
                  setActiveMenuIcon(item.label + id)
                  navigate(`/${node.path}`)
                }}
              />
            ) : (
              <MenuItem
                key={key}
                title={node.label}
                active={node.label + id === activeMenu}
                onClick={() => {
                  setActiveMenu(node.label + id)
                  setActiveMenuIcon(item.label + id)
                  navigate(`/${node.path}`)
                }}
              />
            )
          }
        })}
      </MenuItem>
    )
  }

  return (
    <MenuItem
      title={item.label}
      key={id}
      active={item.label + id === activeMenuIcon || item.label === activeMenu}
      onlyIcon={item.label + id === activeMenuIcon}
      leftAdornment={<Icon iconName={item.icon} iconStyle="fal" size="sm" />}
      onClick={() => {
        setActiveMenuIcon(item.label + id)
        setActiveMenu(item.label)
        navigate(`/${item.path}`)
      }}
    ></MenuItem>
  )
}

export default function Navigation({
  pinLeftNav,
  hideScrollbar,
  setHideScrollbar,
  role,
  flags,
}: {
  pinLeftNav: boolean
  hideScrollbar: boolean
  setHideScrollbar: (pinLeftNav: boolean) => void
  role: Role
  flags?: LDFlagSet
}) {
  const navItems: INavItem[] = useGetNavItems()

  const filteredNavItems = useMemo(() => filterNav(role, flags, navItems), [role, flags, navItems])
  const [activeMenu, setActiveMenu] = useState('')
  const [activeMenuIcon, setActiveMenuIcon] = useState('')
  return (
    <div className={css.left_nav_panel}>
      <NavPanel
        pinned={pinLeftNav}
        defaultState={!pinLeftNav}
        panelHandler={(hover) => setHideScrollbar(hover)}
      >
        <div
          className={css.left_nav_container}
          style={{ overflowY: hideScrollbar ? 'hidden' : auto }}
        >
          {filteredNavItems.map((item: INavItem, id) => (
            <Item
              id={id}
              key={item.path || item.label}
              item={item}
              activeMenu={hideScrollbar ? '' : activeMenu}
              setActiveMenu={setActiveMenu}
              activeMenuIcon={!hideScrollbar ? '' : activeMenuIcon}
              setActiveMenuIcon={setActiveMenuIcon}
            />
          ))}
        </div>
      </NavPanel>
    </div>
  )
}
